import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, BedDouble, BarChart3 } from 'lucide-react';
import axios from 'axios';

export default function AdminReports() {
  const { apiHeaders, language, formatPrice, t, BACKEND_URL } = useApp();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const MONTHS_TR = ['','Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
  const MONTHS_EN = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
  const months = language === 'tr' ? MONTHS_TR : MONTHS_EN;
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/reports/monthly?year=${year}&month=${month}`, apiHeaders())
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [year, month, BACKEND_URL]);

  const kpis = data ? [
    { icon: TrendingUp, label: language === 'tr' ? 'Toplam Gelir' : 'Total Revenue', value: formatPrice(data.total_revenue_try), bg: '#EFE7DA', color: '#1F3A2E', testid: 'admin-reports-revenue-chart' },
    { icon: BedDouble, label: language === 'tr' ? 'Rezervasyon Sayısı' : 'Reservations', value: data.total_reservations, bg: '#dbeafe', color: '#1e40af', testid: 'admin-kpi-reservations' },
    { icon: BarChart3, label: language === 'tr' ? 'Doluluk Oranı' : 'Occupancy Rate', value: `${data.occupancy_percentage}%`, bg: '#dcfce7', color: '#166534', testid: 'admin-reports-occupancy-chart' },
    { icon: TrendingUp, label: language === 'tr' ? 'Ort. Günlük Gelir' : 'Avg Daily Revenue', value: formatPrice(data.adr_try), bg: '#fef3c7', color: '#92400e', testid: 'admin-adr' },
  ] : [];

  const chartData = data?.daily_data?.map(d => ({ day: new Date(d.date).getDate(), revenue: Math.round(d.revenue) })) || [];

  const sel = { display: 'inline-block', padding: '7px 12px', fontSize: '13px', fontWeight: 600, border: '1.5px solid #D8D1C6', borderRadius: '8px', background: 'white', color: '#1F3A2E', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', appearance: 'none', outline: 'none' };

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E', marginBottom: '2px' }}>{t('reports')}</h1>
          <p style={{ fontSize: '13px', color: '#6E7F5B' }}>{language === 'tr' ? 'Aylık gelir ve doluluk raporları' : 'Monthly revenue and occupancy reports'}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select value={month} onChange={e => setMonth(parseInt(e.target.value))} style={sel}>
            {months.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} style={{ ...sel, minWidth: '80px' }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {loading ? (
          [1,2,3,4].map(i => <div key={i} style={{ height: '96px', background: '#F6F1E7', borderRadius: '14px', animation: 'pulse 1.5s infinite' }} />)
        ) : kpis.map(kpi => (
          <div key={kpi.label} style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '18px 20px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px' }}>
              <kpi.icon size={18} style={{ color: kpi.color }} />
            </div>
            <p style={{ fontSize: '20px', fontWeight: 800, color: '#1F3A2E', fontVariantNumeric: 'tabular-nums', marginBottom: '2px' }} data-testid={kpi.testid}>{kpi.value}</p>
            <p style={{ fontSize: '12px', color: '#6E7F5B' }}>{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Chart + Room Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
        {/* Revenue Chart */}
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '20px' }} data-testid="admin-reports-chart-container">
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#1F3A2E', marginBottom: '16px' }}>
            {language === 'tr' ? '📊 Günlük Gelir Dağılımı' : '📊 Daily Revenue Distribution'}
          </p>
          {loading ? (
            <div style={{ height: '200px', background: '#F6F1E7', borderRadius: '8px' }} />
          ) : chartData.length === 0 ? (
            <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#B0A99A', fontSize: '14px' }}>
              {language === 'tr' ? 'Veri yok' : 'No data'}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFE7DA" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#6E7F5B' }} />
                <YAxis tick={{ fontSize: 11, fill: '#6E7F5B' }} />
                <Tooltip
                  contentStyle={{ background: 'white', border: '1px solid #D8D1C6', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v) => [formatPrice(v), language === 'tr' ? 'Gelir' : 'Revenue']}
                />
                <Bar dataKey="revenue" fill="#1F3A2E" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Room Revenue */}
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '20px' }}>
          <p style={{ fontWeight: 700, fontSize: '14px', color: '#1F3A2E', marginBottom: '16px' }}>
            {language === 'tr' ? '🏨 Oda Bazlı Gelir' : '🏨 Revenue by Room'}
          </p>
          {loading ? (
            <div style={{ height: '160px', background: '#F6F1E7', borderRadius: '8px' }} />
          ) : !data || Object.keys(data.room_revenue || {}).length === 0 ? (
            <p style={{ fontSize: '13px', color: '#B0A99A', textAlign: 'center', padding: '30px 0' }}>{language === 'tr' ? 'Veri yok' : 'No data'}</p>
          ) : (
            <div>
              {Object.entries(data.room_revenue).map(([roomName, info]) => (
                <div key={roomName} style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #F6F1E7' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#1F3A2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>{roomName}</p>
                    <p style={{ fontSize: '13px', fontWeight: 800, color: '#B08D57', fontVariantNumeric: 'tabular-nums' }}>{formatPrice(info.revenue)}</p>
                  </div>
                  <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{info.bookings} {language === 'tr' ? 'rezerv.' : 'res.'} · {info.nights} {t('nights')}</p>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '14px', color: '#1F3A2E', paddingTop: '4px' }}>
                <span>{language === 'tr' ? 'Toplam' : 'Total'}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPrice(data.total_revenue_try)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {data && !loading && (
        <div style={{ background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', textAlign: 'center' }}>
            {[
              { label: language === 'tr' ? 'Toplam Gece' : 'Total Nights', value: data.total_nights },
              { label: language === 'tr' ? 'Ortalama Konaklama' : 'Avg. Stay', value: `${data.total_reservations > 0 ? (data.total_nights / data.total_reservations).toFixed(1) : '0'} ${t('nights')}` },
              { label: language === 'tr' ? 'Doluluk Oranı' : 'Occupancy Rate', value: `${data.occupancy_percentage}%` },
              { label: language === 'tr' ? 'Ort. Gece Fiyatı' : 'Avg. Night Rate', value: formatPrice(data.adr_try) },
            ].map(s => (
              <div key={s.label}>
                <p style={{ fontSize: '22px', fontWeight: 800, color: '#1F3A2E', marginBottom: '2px', fontVariantNumeric: 'tabular-nums' }}>{s.value}</p>
                <p style={{ fontSize: '12px', color: '#6E7F5B' }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
